import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Package, Plus, Search, Loader2, Pencil, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const VAT_OPTIONS = [0, 4, 10, 22];

const emptyForm = () => ({
  product_code: '', description: '', lot_number: '', unit_measure: 'NR',
  quantita_per_collo: 1, colli_default: 1, peso_lordo: 0, volume: 0,
  unit_price: 0, vat_rate: 4,
  packaging_id: '', packaging_name: '',
});

export default function Products() {
  const { canCreate, canEdit, canDelete } = usePermissions();
  const [products, setProducts] = useState([]);
  const [packagings, setPackagings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const fetchProducts = async () => {
    try {
      const [prodRes, pkgRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/packaging`).catch(() => ({ data: [] })),
      ]);
      setProducts(prodRes.data);
      setPackagings(pkgRes.data);
    } catch { toast.error('Errore caricamento articoli'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProducts(); }, []);

  const openCreate = () => { setForm(emptyForm()); setEditId(null); setShowForm(true); };
  const openEdit = (p) => {
    setForm({
      product_code: p.product_code || '',
      description: p.description || '',
      lot_number: p.lot_number || '',
      unit_measure: p.unit_measure || 'NR',
      quantita_per_collo: p.quantita_per_collo ?? p.quantity_default ?? 1,
      colli_default: p.colli_default ?? 1,
      peso_lordo: typeof p.peso_lordo === 'number' ? p.peso_lordo : (parseFloat(p.peso_lordo) || 0),
      volume: typeof p.volume === 'number' ? p.volume : (parseFloat(p.volume) || 0),
      unit_price: p.unit_price ?? 0,
      vat_rate: p.vat_rate ?? 4,
      packaging_id: p.packaging_id || '',
      packaging_name: p.packaging_name || '',
    });
    setEditId(p._id || p.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.description) { toast.error('Inserisci la descrizione'); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/products/${editId}`, form);
        toast.success('Articolo aggiornato');
      } else {
        await axios.post(`${API}/products`, form);
        toast.success('Articolo creato');
      }
      setShowForm(false);
      fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, desc) => {
    if (!window.confirm(`Eliminare "${desc}"?`)) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success('Articolo eliminato');
      fetchProducts();
    } catch { toast.error('Errore'); }
  };

  const f = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const filtered = products.filter(p =>
    !search || [p.product_code, p.description, p.lot_number]
      .filter(Boolean).some(v => v.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Catalogo Articoli</h1>
          <p className="text-muted-foreground text-sm mt-1">{products.length} articoli nel catalogo</p>
        </div>
        {canCreate('orders') && (
          <Button onClick={openCreate} data-testid="add-product-btn">
            <Plus size={16} className="mr-2" />Nuovo Articolo
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Totale Articoli', value: products.length },
          { label: 'IVA 4%',   value: products.filter(p => p.vat_rate === 4).length },
          { label: 'IVA 10%',  value: products.filter(p => p.vat_rate === 10).length },
          { label: 'IVA 22%',  value: products.filter(p => p.vat_rate === 22).length },
        ].map(({ label, value }) => (
          <Card key={label}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-2xl font-bold mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Cerca codice, descrizione, lotto..." className="pl-9 h-9" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Package size={48} className="text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold text-lg mb-1">Nessun articolo</h3>
          <p className="text-muted-foreground text-sm mb-4">Aggiungi i prodotti del tuo catalogo</p>
          {canCreate('orders') && (
            <Button onClick={openCreate}><Plus size={14} className="mr-2" />Aggiungi Articolo</Button>
          )}
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: '900px' }}>
              <thead>
                <tr className="bg-muted/50 border-b">
                  {['Codice', 'Descrizione', 'Lotto', 'U.M.', 'Qtà/Collo', 'Colli', 'Peso Lordo', 'Volume', 'Imballaggio', 'IVA', 'Prezzo €', ''].map(h => (
                    <th key={h} className={`px-3 py-2.5 font-medium text-muted-foreground text-xs uppercase tracking-wider ${h ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const pid = p._id || p.id;
                  return (
                    <tr key={pid} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-3 py-2.5 font-mono text-xs font-semibold text-primary">{p.product_code || '-'}</td>
                      <td className="px-3 py-2.5 font-medium max-w-xs">{p.description}</td>
                      <td className="px-3 py-2.5 text-muted-foreground font-mono text-xs">{p.lot_number || '-'}</td>
                      <td className="px-3 py-2.5 text-center">{p.unit_measure || 'NR'}</td>
                      <td className="px-3 py-2.5 text-center">{p.quantita_per_collo ?? p.quantity_default ?? 1}</td>
                      <td className="px-3 py-2.5 text-center">{p.colli_default ?? 1}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{p.peso_lordo > 0 ? `${p.peso_lordo} g` : '-'}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{p.volume > 0 ? `${p.volume} m³` : '-'}</td>
                      <td className="px-3 py-2.5 text-muted-foreground text-xs">
                        {p.packaging_name ? <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">{p.packaging_name}</span> : '-'}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="text-xs px-1.5 py-0.5 rounded bg-muted font-medium">{p.vat_rate ?? 4}%</span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-medium">
                        {p.unit_price > 0 ? `€ ${Number(p.unit_price).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : '-'}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canEdit('orders') && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)} data-testid={`edit-product-${pid}`}>
                              <Pencil size={13} />
                            </Button>
                          )}
                          {canDelete('orders') && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(pid, p.description)} data-testid={`delete-product-${pid}`}>
                              <Trash2 size={13} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={v => { setShowForm(v); if (!v) setForm(emptyForm()); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editId ? 'Modifica Articolo' : 'Nuovo Articolo'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Codice Articolo</Label>
                <Input value={form.product_code} onChange={e => f('product_code', e.target.value)} placeholder="es. 000892701" data-testid="product-code-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Lotto</Label>
                <Input value={form.lot_number} onChange={e => f('lot_number', e.target.value)} placeholder="es. 1152002" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Descrizione *</Label>
              <Input value={form.description} onChange={e => f('description', e.target.value)} placeholder="es. VERLIZIE INSALATE G150 6pz" required data-testid="product-desc-input" />
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label>U.M.</Label>
                <Input value={form.unit_measure} onChange={e => f('unit_measure', e.target.value)} placeholder="NR" />
              </div>
              <div className="space-y-1.5">
                <Label>Qtà per Collo</Label>
                <Input type="number" min="0" step="0.01" value={form.quantita_per_collo} onChange={e => f('quantita_per_collo', parseFloat(e.target.value) || 0)} data-testid="product-qtapercollo-input" />
              </div>
              <div className="space-y-1.5">
                <Label>N. Colli (default)</Label>
                <Input type="number" min="0" step="1" value={form.colli_default} onChange={e => f('colli_default', parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-1.5">
                <Label>IVA %</Label>
                <Select value={String(form.vat_rate)} onValueChange={v => f('vat_rate', parseFloat(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{VAT_OPTIONS.map(r => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Peso per unità (g)</Label>
                <Input type="number" min="0" step="0.01" value={form.peso_lordo} onChange={e => f('peso_lordo', parseFloat(e.target.value) || 0)} placeholder="es. 100" data-testid="product-peso-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Volume per unità (m³)</Label>
                <Input type="number" min="0" step="0.001" value={form.volume} onChange={e => f('volume', parseFloat(e.target.value) || 0)} placeholder="es. 0.500" data-testid="product-volume-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Prezzo per unità €</Label>
                <Input type="number" min="0" step="0.01" value={form.unit_price} onChange={e => f('unit_price', parseFloat(e.target.value) || 0)} />
              </div>
            </div>

            {/* Packaging selector — default for this article */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-2">
                Imballaggio di default
                <span className="text-[10px] text-muted-foreground font-normal">(opzionale — auto-caricato in ordini)</span>
              </Label>
              <select
                value={form.packaging_id || ''}
                onChange={e => {
                  const pkgId = e.target.value;
                  const pkg = packagings.find(p => (p._id || p.id) === pkgId);
                  setForm(prev => ({
                    ...prev,
                    packaging_id: pkgId,
                    packaging_name: pkg ? pkg.name : '',
                  }));
                }}
                className="h-9 text-sm w-full border border-input rounded-md px-3 bg-background"
                data-testid="product-packaging-select"
              >
                <option value="">— nessuno —</option>
                {packagings.map(p => (
                  <option key={p._id || p.id} value={p._id || p.id}>
                    {p.name} ({p.length_cm}×{p.width_cm}×{p.height_cm} cm — {Number(p.volume_m3 || 0).toFixed(4)} m³)
                  </option>
                ))}
              </select>
              {packagings.length === 0 && (
                <p className="text-xs text-muted-foreground">Nessun imballaggio nel catalogo. Creane uno dalla voce <b>Imballaggi</b> nel menu laterale.</p>
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="save-product-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editId ? 'Aggiorna' : 'Crea Articolo'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
